import type { MiceTraveler } from '@voll-intelligence/domain';
import { useRef, useState } from 'react';
import { miceRepository } from '@web/data/repositories';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { useDebouncedValue } from '@web/ui/core/hooks';
import type { MiceTravelerFilter } from '../travelers/traveler_filter';
import { errorMessage } from './use_mice_orders';
import {
  TRAVELERS_PAGE_SIZE,
  useMiceTravelers,
  type MiceTravelersFilter,
} from './use_mice_travelers';

/** Espera depois da última tecla antes de buscar no servidor. */
const SEARCH_DEBOUNCE_MS = 300;
/** Teto de `pageSize` no servidor: "selecionar todos" lê o filtro inteiro nessas páginas. */
const SELECT_ALL_PAGE_SIZE = 200;

const NO_FILTER: MiceTravelerFilter = {
  phase: 'all',
  text: '',
  alert: 'any',
  origin: 'any',
  color: 'any',
};

/** Todas as páginas do filtro, para selecionar quem ainda não foi carregado. */
async function fetchFilter(
  orgId: string,
  projectId: string,
  filter: MiceTravelersFilter,
): Promise<MiceTraveler[]> {
  const out: MiceTraveler[] = [];
  for (let page = 1; ; page += 1) {
    const res = await miceRepository.listTravelers(orgId, projectId, {
      ...filter,
      page,
      pageSize: SELECT_ALL_PAGE_SIZE,
    });
    out.push(...res.items);
    if (page * SELECT_ALL_PAGE_SIZE >= res.total) return out;
  }
}

/**
 * Estado da lista de viajantes na tela. Os filtros vão para o servidor (que conta por fase o
 * resultado dos outros filtros); trocar um filtro limpa a seleção.
 */
export function useTravelersView(projectId: string) {
  const { orgId } = useOrgContext();
  const { showSnackbar } = useSnackbar();
  const [filter, setFilter] = useState<MiceTravelerFilter>(NO_FILTER);
  const q = useDebouncedValue(filter.text, SEARCH_DEBOUNCE_MS);
  const server: MiceTravelersFilter = {
    phase: filter.phase,
    q,
    alert: filter.alert,
    origin: filter.origin,
    color: filter.color,
  };
  const data = useMiceTravelers(projectId, server);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [selection, setSelection] = useState<ReadonlyMap<string, MiceTraveler>>(new Map());
  // Cada mudança de seleção anda a geração: um "selecionar todos" que volta depois dela é descartado.
  const generation = useRef(0);
  const select = (next: ReadonlyMap<string, MiceTraveler>): void => {
    generation.current += 1;
    setSelection(next);
  };

  // Só quem tem pedido expande (as sub-linhas são os pedidos).
  const expandable = data.travelers.filter((tr) => tr.orders.length > 0);
  const allOpen = expandable.length > 0 && expandable.every((tr) => expanded.has(tr.id));
  // A seleção mostra a versão mais nova de quem está carregado.
  const byId = new Map(data.travelers.map((tr) => [tr.id, tr]));
  const selected = [...selection.values()].map((tr) => byId.get(tr.id) ?? tr);
  const allSelected = data.total > 0 && selected.length >= data.total;
  let selectState: boolean | 'indeterminate' = selected.length > 0 ? 'indeterminate' : false;
  if (allSelected) selectState = true;

  return {
    ...data,
    filter,
    setFilter: (next: Partial<MiceTravelerFilter>) => {
      // Começar uma busca (do vazio para um texto) procura em todas as fases.
      const startsSearch =
        next.text !== undefined && filter.text.trim() === '' && next.text.trim() !== '';
      const merged = { ...filter, ...(startsSearch ? { phase: 'all' as const } : {}), ...next };
      // A cor só existe em "Para emitir": sair da fase leva o filtro junto.
      setFilter(merged.phase === 'approval' ? merged : { ...merged, color: 'any' });
      select(new Map());
    },
    /** Quantas linhas o próximo "Mostrar mais" acrescenta (0 = nenhuma). */
    moreCount: data.hasMore ? Math.min(TRAVELERS_PAGE_SIZE, data.total - data.travelers.length) : 0,
    expanded,
    allOpen,
    toggle: (id: string) => {
      const next = new Set(expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setExpanded(next);
    },
    selected,
    selectedIds: new Set(selection.keys()) as ReadonlySet<string>,
    /** `true` com todos do filtro marcados; `indeterminate` com parte deles. */
    selectState,
    toggleSelected: (id: string, on: boolean) => {
      const next = new Map(selection);
      const traveler = byId.get(id);
      if (on && traveler) next.set(id, traveler);
      else next.delete(id);
      select(next);
    },
    /** Marca ou desmarca todos os viajantes do filtro, não só os das páginas lidas. */
    selectAll: async (on: boolean): Promise<void> => {
      if (!on) {
        select(new Map());
        return;
      }
      const started = (generation.current += 1);
      try {
        // O texto como está agora, não o da última pausa: a lista vai mostrar este filtro.
        const all = await fetchFilter(orgId, projectId, { ...server, q: filter.text.trim() });
        if (generation.current === started) setSelection(new Map(all.map((tr) => [tr.id, tr])));
      } catch (err: unknown) {
        showSnackbar({ severity: 'error', message: errorMessage(err) });
      }
    },
    clearSelection: () => {
      select(new Map());
    },
    toggleAll: () => {
      const next = new Set(expanded);
      for (const tr of expandable) {
        if (allOpen) next.delete(tr.id);
        else next.add(tr.id);
      }
      setExpanded(next);
    },
  };
}
